import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FilterAltRounded from '@mui/icons-material/FilterAltRounded';
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { Catalog as CatalogDict } from '@/shared/i18n';
import { CatalogFilters } from '../../types/filter';
import { CourseDataFilters } from '../../types/filtered-data';
import { Instrument } from '../../types/instrument';
import { instrumentHasChildren, getChildInstruments } from '../../utils/common';
import { FilterAccordion } from './filter-accordion';
import { LevelSlider } from './filter-level-slider';
import { adaptCourseTypeText } from 'src/shared/utils/adapt-course-type-text';

interface FilterMobileProps {
  dict: CatalogDict;
  filterData: CatalogFilters;
  activeFilters: CourseDataFilters;
  instrumentsFilterOnChange: (checked: boolean, instrument: Instrument) => void;
  handleSkillLevelsChange: (selectedLevels: string[] | null) => void;
  handleGenresChange: (genre: string, checked: boolean) => void;
  handleTopicsChange: (topic: string, checked: boolean) => void;
  handleTypeChange: (type: string, checked: boolean) => void;
  isEnrichingData?: boolean;
  onResetSidebarFilters: () => void;
  isAnyFilterActive: boolean;
}

export const FilterMobile = ({
  dict,
  filterData,
  activeFilters,
  instrumentsFilterOnChange,
  handleSkillLevelsChange,
  handleGenresChange,
  handleTopicsChange,
  handleTypeChange,
  onResetSidebarFilters,
}: FilterMobileProps) => {
  const [open, setOpen] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down'>('down');

  const handleToggle = () => {
    if (open) {
      setSlideDirection('up');
    } else {
      setSlideDirection('down');
    }
    setOpen(!open);
  };

  const parentInstruments = filterData?.instruments?.filter((inst) => !inst.parentInstrument);
  const selectedParentInstruments = filterData?.instruments?.filter((inst) => {
    return (
      activeFilters.instruments.includes(inst.instrumentName) &&
      instrumentHasChildren(inst, filterData.instruments)
    );
  });

  // Check if any filters are active
  const hasActiveFilters =
    activeFilters.instruments.length > 0 ||
    activeFilters.skillLevels.length > 0 ||
    activeFilters.genres.length > 0 ||
    activeFilters.topics.length > 0 ||
    activeFilters.types.length > 0;

  return (
    <div className="block md:hidden">
      <Button
        variant="outlined"
        color="primary"
        size="large"
        className="w-full gap-2"
        onClick={handleToggle}
        disabled={false} // Filters should never be disabled
      >
        <FilterAltRounded />
        <Typography variant="button-l">Mostra filtri</Typography>
        {hasActiveFilters && <div className="w-[10px] h-[10px] bg-[#FBE34E] rounded-xl" />}
      </Button>

      <Dialog fullScreen open={open} onClose={handleToggle}>
        <DialogTitle
          variant="h5"
          component="h5"
          className="flex items-center justify-between py-4 border-b border-neutral-main"
          sx={{ paddingRight: '0 !important' }}
        >
          <Typography variant="h5">Filtri</Typography>
          <Button size="small" onClick={handleToggle} variant="text">
            <Typography variant="button-s">Chiudi</Typography>
          </Button>
        </DialogTitle>

        <DialogContent className="p-6">
          {/* Child instruments accordions */}
          {selectedParentInstruments?.map((parent, idx) => {
            const childInstruments = getChildInstruments(parent, filterData.instruments);
            return (
              <FilterAccordion
                defaultExpanded
                key={idx}
                title={`Tipo ${parent.instrumentName.toLowerCase()}`}
                accordionSummaryClassName="px-0"
                accordionDetailsClassName="px-0"
              >
                <FormGroup>
                  {childInstruments.map((child, childIndex) => (
                    <FormControlLabel
                      key={childIndex}
                      label={child.instrumentName}
                      control={
                        <Checkbox
                          checked={activeFilters.instruments.includes(child.instrumentName)}
                          onChange={(e) => instrumentsFilterOnChange(e.target.checked, child)}
                        />
                      }
                    />
                  ))}
                </FormGroup>
              </FilterAccordion>
            );
          })}

          {/* Skill Levels */}
          <div className="mb-4">
            <LevelSlider
              onChange={handleSkillLevelsChange}
              selectedLevels={activeFilters.skillLevels}
            />
          </div>

          {/* Course Types */}
          <FilterAccordion
            title="Tipologia"
            accordionSummaryClassName="px-0"
            accordionDetailsClassName="px-0"
          >
            <FormGroup>
              {filterData?.sidebar?.type?.map((t, index) => (
                <FormControlLabel
                  key={index}
                  label={adaptCourseTypeText(t)}
                  control={
                    <Checkbox
                      checked={activeFilters.types.includes(t)}
                      onChange={(e) => handleTypeChange(t, e.target.checked)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </FilterAccordion>

          {/* Genres */}
          <FilterAccordion
            title="Genere"
            accordionSummaryClassName="px-0"
            accordionDetailsClassName="px-0"
          >
            <FormGroup>
              {filterData?.sidebar?.genres?.map((genre, index) => (
                <FormControlLabel
                  key={index}
                  label={genre.name}
                  control={
                    <Checkbox
                      checked={activeFilters.genres.includes(genre.name)}
                      onChange={(e) => handleGenresChange(genre.name, e.target.checked)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </FilterAccordion>

          {/* Topics */}
          <FilterAccordion
            title="Argomento"
            accordionSummaryClassName="px-0"
            accordionDetailsClassName="px-0"
          >
            <FormGroup>
              {filterData?.sidebar?.topics?.map((topic, index) => (
                <FormControlLabel
                  key={index}
                  label={topic.name}
                  control={
                    <Checkbox
                      checked={activeFilters.topics.includes(topic.name)}
                      onChange={(e) => handleTopicsChange(topic.name, e.target.checked)}
                    />
                  }
                />
              ))}
            </FormGroup>
          </FilterAccordion>
        </DialogContent>

        <DialogActions className="flex-col sm:flex-row gap-2">
          <Button
            size="medium"
            variant="outlined"
            color="primary"
            className="w-full"
            onClick={onResetSidebarFilters}
            disabled={!hasActiveFilters}
          >
            <Typography variant="button-m">Annulla filtri</Typography>
          </Button>
          <Button
            size="medium"
            variant="contained"
            color="primary"
            className="w-full ml-0"
            onClick={handleToggle}
          >
            <Typography variant="button-m">Applica</Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
